import { defineStore } from 'pinia';
import type { IStoreState } from '@domain/interfaces/store/IStoreState';
import type { IStoreGetters } from '@domain/interfaces/store/IStoreGetters';
import type { IStoreActions } from '@domain/interfaces/store/IStoreActions';
import type IPageData from '@app/interfaces/dtos/IPageData';
import type { ITuitionFeeRange } from '@app/interfaces/dtos/ITuitionFeeRange';
import { environmentAwareFetch } from '@utils/environment-aware-fetch';
import type { ICurrencyConversionService } from '@studyportals/portal-localized-content';
import { useEventSender } from '@composables/useEventSender';
import { getPortalTypeFromEnvClient } from "@utils/get-portal-type-from-env.client";
import { Router } from 'vue-router';
import { RouteMatchResult } from '@domain/interfaces/mappers/RouteMatchResult';
import { SearchResultType } from '@domain/enums/SearchResultType';
import { SearchTabText } from '../../domain/enums/SearchTabText';
import { useCookie } from "nuxt/app";
import { FilterProcessingService } from "@app/routing/FilterProcessingService";
import { FilterType } from '@domain/enums/FilterType';
import { RoutingDefinitions } from '@generator/types/RoutingDefinitions';
import { Pagination } from '@domain/enums/Pagination';

const filtersProcessingService = FilterProcessingService.getInstance();

export const useSearchStore = defineStore<
	"search",
	IStoreState,
	IStoreGetters,
	IStoreActions
>("search", {
	state: (): IStoreState => {
		return {
			pageData: null,
			filtersSelection: [],
			loading: false,
			externalServicesLoaded: false,
			portalType: getPortalTypeFromEnvClient(),
			currencyConversionService: {} as ICurrencyConversionService,
			isProfileQuestionnairesReady: false,
			tuitionFeeRange: null,
			userNationality: "NL",
			userCountryIso: "NL",
			userCurrency: "EUR",
			isGAIQuestionnaireTaken: true,
			searchType: SearchResultType.PROGRAMME_SEARCH,
			currentPage: Pagination.DEFAULT_PAGE,
			offset: Pagination.DEFAULT_OFFSET,
		};
	},
	actions: {
		async loadPageData(router: Router): Promise<void> {
			try {
				this.loading = true;
				this.currentPage = router.currentRoute.value.query.page
					? parseInt(router.currentRoute.value.query.page as string, 10)
					: this.currentPage;
				this.offset = (this.currentPage - 1) * Pagination.PAGE_SIZE;

				// Capture SSR-sensitive composables synchronously before any await
				const portalMap = import.meta.server ? useCookie("PortalMap") : window.PortalMap;

				this.filtersSelection = await filtersProcessingService.processRoute(
					router?.currentRoute.value
				);
				const sortBy = router?.currentRoute.value.query.sorting as | string | undefined;
				// Assuming there's a sort filter in the filtersSelection
				const nonExtendedFiltersSelection = this.filtersSelection.map(
					(filter) => ({
						type: filter.type,
						values: filter.values.map((value) => value.id),
					})
				);

				const data = await environmentAwareFetch<IPageData | null>(
					"/api/search",
					{
						method: "POST",
						body: {
							filters: nonExtendedFiltersSelection,
							sortBy,
							resultOffset: this.offset,
							userData: {
								nationality_country_iso: this.userNationality,
								residence_country_iso: this.userCountryIso,
								currency: this.userCurrency,
								isGAIQuestionnaireTaken: this.isGAIQuestionnaireTaken,
							},
							portalMap,
						},
					}
				);
				this.pageData = data;
			} catch (error: unknown) {
				const parsedError = error instanceof Error? error : new Error("Failed to load page data");
				return Promise.reject(parsedError);
			} finally {
				this.loading = false;
			}

			return Promise.resolve();
		},
		loadExternalServices(): void {
			this.externalServicesLoaded = true;
		},
		setSearchTab(searchType: SearchTabText): void {
			switch (searchType) {
			case SearchTabText.PROGRAMMES:
				this.searchType = SearchResultType.PROGRAMME_SEARCH;
				break;
			case SearchTabText.UNIVERSITIES:
				this.searchType = SearchResultType.UNIVERSITY_SEARCH;
				break;
			case SearchTabText.SCHOLARSHIPS:
				this.searchType = SearchResultType.SCHOLARSHIP_SEARCH;
				break;
			}
		},
		async processCurrencyOrCountryChange(
			newUserCountryIso?: string,
			newUserCurrency?: string
		): Promise<void> {
			let changed = false;

			if (newUserCurrency && newUserCurrency !== this.userCurrency) {
				this.userCurrency = newUserCurrency;
				changed = true;
			}

			if (newUserCountryIso && newUserCountryIso !== this.userCountryIso) {
				this.userCountryIso = newUserCountryIso;
				changed = true;
			}

			if (changed) {
				useEventSender().sendInitialEvents(this.portalType);
				// TODO Reload Search
			}
		},
		async setPage(router: Router, pageNumber: number): Promise<void> {
			this.currentPage = pageNumber;
			this.offset = (pageNumber - 1) * Pagination.PAGE_SIZE;
			await router.replace({
				query: {
					...router.currentRoute.value.query,
					page: pageNumber.toString(),
				}
			});
			await this.loadPageData(router);

		},
		setProfileQuestionnairesReady(ready: boolean): void {
			this.isProfileQuestionnairesReady = ready;
		},
		setTuitionFeeRange(range: ITuitionFeeRange): void {
			this.tuitionFeeRange = range;
		},
		async setFiltersFromRoute(router?: Router): Promise<void> {
			if (!router || !router.currentRoute) return;
			this.filtersSelection = await filtersProcessingService.processRoute(
				router.currentRoute.value
			);
		},
		async setRouteFromFilters(router?: Router): Promise<void> {
			if (!router) return;

			const mappedRoute: RouteMatchResult | undefined =
        filtersProcessingService.processFilters(router, this.filtersSelection);

			await router.replace({
				name: mappedRoute.name,
				params: mappedRoute.params,
				query: mappedRoute.query,
				hash: mappedRoute.hash,
			});
			this.currentPage = Pagination.DEFAULT_PAGE;
		},
		async cleanFilters(router: Router): Promise<void> {
			this.filtersSelection = [];

			await router.push({
				name: RoutingDefinitions.PORTAL_ROUTE,
				params: { [FilterType.EDUCATION_LEVEL]: this.portalType },
			});
			this.currentPage = Pagination.DEFAULT_PAGE;
			await this.loadPageData(router);
		},
		async removeFilterValueByType(
			filterType: FilterType,
			filterValue: string,
			router: Router
		): Promise<void> {
			const filterTypeTarget = this.filtersSelection.find(
				(f) => f.type === filterType
			);
			if (!filterTypeTarget) return;

			if (filterTypeTarget.values.length === 1) {
				this.filtersSelection = this.filtersSelection.filter(
					(f) => f.type !== filterType
				);
			} else {
				filterTypeTarget.values = filterTypeTarget.values.filter(
					(v) => v.id !== filterValue
				);
			}

			await this.setRouteFromFilters(router);
			this.currentPage = Pagination.DEFAULT_PAGE;
			await this.loadPageData(router);
		},
		async removeFilterByType(
			filterType: FilterType,
			router: Router
		): Promise<void> {
			this.filtersSelection = this.filtersSelection.filter(
				(f) => f.type !== filterType
			);

			await this.setRouteFromFilters(router);
			await this.loadPageData(router);
		},
	},
});
